import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { fetchComplianceJson } from "./complianceFetch";
import { complianceQueryAtom } from "./complianceLive";
import type { OrgProfileUnavailableReason } from "./orgProfile";

const CommentSchema = Schema.Struct({
  id: Schema.String,
  text: Schema.String,
  actorId: Schema.NullOr(Schema.String),
  actorEmail: Schema.NullOr(Schema.String),
  actorAvatarUrl: Schema.optionalKey(Schema.NullOr(Schema.String)),
  createdAt: Schema.String,
});
const PageSchema = Schema.Struct({
  comments: Schema.Array(CommentSchema),
  nextCursor: Schema.NullOr(Schema.String),
});
export type SuggestionComment = typeof CommentSchema.Type;
export type SuggestionCommentSnapshot = typeof PageSchema.Type;
const decodePage = Schema.decodeUnknownEffect(PageSchema);
type CommentKind = "suggestion" | "finding";
const target = (
  organizationId: string,
  recommendationId: string,
  before?: string,
  kind: CommentKind = "suggestion",
) =>
  resolvePrimaryEnvironmentHttpUrl(`/api/amoreg/regulation/${kind}-comments`, {
    organizationId,
    [kind === "finding" ? "findingId" : "recommendationId"]: recommendationId,
    ...(before ? { before } : {}),
  });

/** Append-only conversations retain already-loaded history across live page shifts. */
export function mergeSuggestionComments(
  previous: SuggestionCommentSnapshot | null,
  current: SuggestionCommentSnapshot,
): SuggestionCommentSnapshot {
  const comments = new Map(previous?.comments.map((comment) => [comment.id, comment]));
  // A refreshed latest page can point at history we already fully loaded.
  // Require overlap: a burst larger than the page may leave an unknown gap.
  const exhausted =
    previous !== null &&
    previous.comments.length > 0 &&
    previous.nextCursor === null &&
    current.comments.some((comment) => comments.has(comment.id));
  for (const comment of current.comments) comments.set(comment.id, comment);
  return {
    comments: [...comments.values()].sort(
      (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
    ),
    nextCursor: exhausted ? null : current.nextCursor,
  };
}

export const suggestionCommentsAtom = Atom.family((key: string) => {
  const [organizationId, recommendationId, pages, kind = "suggestion"] = JSON.parse(key) as [
    string,
    string,
    number,
    CommentKind?,
  ];
  return complianceQueryAtom(
    organizationId,
    Effect.gen(function* () {
      if (!organizationId || !recommendationId) return null;
      let snapshot: SuggestionCommentSnapshot = { comments: [], nextCursor: null };
      for (let index = 0; index < pages; index++) {
        const page = yield* fetchComplianceJson(
          target(organizationId, recommendationId, snapshot.nextCursor ?? undefined, kind),
          decodePage,
        );
        if ("unavailable" in page) return page;
        snapshot = mergeSuggestionComments(snapshot, page);
        if (page.nextCursor === null) break;
      }
      return snapshot;
    }),
  ).pipe(Atom.withLabel(`web-amoreg:suggestion-comments:${key}`));
});

/** A stable request id survives ambiguous failures; simultaneous clicks share one request. */
export function createCommentSubmission(url: string, onPosted: () => void) {
  let attempt: { text: string; requestId: string } | null = null;
  let pending: Promise<string | null> | null = null;
  return (raw: string): Promise<string | null> => {
    const text = raw.trim();
    if (!text) return Promise.resolve("Write a comment first.");
    if (text.length > 4000) return Promise.resolve("Comments are limited to 4000 characters.");
    if (pending)
      return attempt?.text === text ? pending : Promise.resolve("A comment is still being posted.");
    if (attempt?.text !== text) attempt = { text, requestId: crypto.randomUUID() };
    const requestId = attempt.requestId;
    pending = Promise.resolve().then(async () => {
      try {
        const response = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ text, requestId }),
        });
        const body: unknown = await response.json().catch(() => null);
        if (!response.ok) {
          return body &&
            typeof body === "object" &&
            "message" in body &&
            typeof body.message === "string"
            ? body.message
            : "The comment could not be posted. Your draft has been kept.";
        }
        if (!body || typeof body !== "object" || !("id" in body) || body.id !== requestId)
          return "The comment could not be confirmed. Try again with the same draft.";
        attempt = null;
        onPosted();
        return null;
      } catch {
        return "The comment could not be posted. Your draft has been kept.";
      } finally {
        pending = null;
      }
    });
    return pending;
  };
}

function useReviewComments(
  organizationId: string | null,
  recommendationId: string | null,
  kind: CommentKind,
) {
  const scope = JSON.stringify([organizationId ?? "", recommendationId ?? "", kind]);
  const [history, setHistory] = useState({ scope, pages: 1 });
  const pages = history.scope === scope ? history.pages : 1;
  const atom = useMemo(
    () =>
      suggestionCommentsAtom(
        JSON.stringify([organizationId ?? "", recommendationId ?? "", pages, kind]),
      ),
    [organizationId, recommendationId, pages, kind],
  );
  const result = useAtomValue(atom);
  const value = AsyncResult.value(result);
  const data = value._tag === "Some" ? value.value : null;
  const unavailableReason: OrgProfileUnavailableReason | null =
    data && "unavailable" in data ? data.unavailable : result._tag === "Failure" ? "error" : null;
  const previous = useRef<{ scope: string; snapshot: SuggestionCommentSnapshot } | null>(null);
  const prior = previous.current?.scope === scope ? previous.current.snapshot : null;
  const snapshot = useMemo(
    () =>
      unavailableReason
        ? null
        : data && !("unavailable" in data)
          ? mergeSuggestionComments(prior, data)
          : result.waiting
            ? prior
            : null,
    [data, prior, result.waiting, unavailableReason],
  );
  useEffect(() => {
    previous.current = snapshot ? { scope, snapshot } : null;
  }, [scope, snapshot]);
  const refresh = useCallback(() => appAtomRegistry.refresh(atom), [atom]);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const post = useMemo(
    () =>
      createCommentSubmission(
        target(organizationId ?? "", recommendationId ?? "", undefined, kind),
        () => refreshRef.current(),
      ),
    [organizationId, recommendationId, kind],
  );
  const postComment = useCallback(
    (text: string) =>
      organizationId && recommendationId ? post(text) : Promise.resolve("Select a review first."),
    [organizationId, recommendationId, post],
  );
  const loadOlder = useCallback(() => {
    if (!result.waiting && snapshot?.nextCursor) setHistory({ scope, pages: pages + 1 });
  }, [result.waiting, snapshot?.nextCursor, scope, pages]);
  return {
    snapshot,
    unavailableReason,
    isPending: result.waiting,
    refresh,
    postComment,
    loadOlder,
    hasMore: snapshot?.nextCursor !== null && snapshot?.nextCursor !== undefined,
    isLoadingOlder: result.waiting && pages > 1 && prior !== null,
  };
}

export const useSuggestionComments = (
  organizationId: string | null,
  recommendationId: string | null,
) => useReviewComments(organizationId, recommendationId, "suggestion");
export const useFindingComments = (organizationId: string | null, findingId: string | null) =>
  useReviewComments(organizationId, findingId, "finding");
